import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock3,
  Download,
  Eye,
  FileCheck2,
  FileText,
  Filter,
  Link2,
  MailCheck,
  MailQuestion,
  Paperclip,
  Plus,
  ReceiptText,
  RefreshCcw,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Upload,
  WalletCards,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "pendencias"
  | "enviadas"
  | "aguardando-envio"
  | "canceladas";

type TipoNotaFiscal = "servico" | "produto" | "mista";

type SituacaoFiscal =
  | "rascunho"
  | "regular"
  | "aguardando_pdf"
  | "aguardando_xml"
  | "inconsistente"
  | "cancelada"
  | "substituida";

type StatusEnvioCliente =
  | "nao_iniciado"
  | "parcial"
  | "concluido"
  | "pendente_validacao"
  | "nao_aplicavel";

type StatusVinculoRecebivel =
  | "sem_recebivel"
  | "recebivel_aberto"
  | "recebivel_vencido"
  | "recebivel_recebido";

type TipoDocumentoFiscal = "pdf" | "xml" | "cancelamento" | "substituicao";

type StatusDocumentoFiscal = "anexado" | "pendente" | "nao_aplicavel";

type DestinoEnvio =
  | "solicitante"
  | "fiscal_cliente"
  | "compras_cliente"
  | "portal_cliente"
  | "financeiro_cliente";

type StatusItemEnvio = "enviado" | "pendente" | "nao_aplicavel";

type DocumentoFiscal = {
  tipo: TipoDocumentoFiscal;
  label: string;
  status: StatusDocumentoFiscal;
  arquivoNome: string | null;
  anexadoEm: string | null;
  responsavel: string | null;
};

type ProcedimentoEnvioItem = {
  id: number;
  destino: DestinoEnvio;
  descricao: string;
  documento: "pdf" | "xml" | "pdf_xml" | "outro";
  contato: string;
  canal: "email" | "portal" | "whatsapp" | "outro";
  status: StatusItemEnvio;
  enviadoEm: string | null;
  responsavel: string | null;
};

type HistoricoNota = {
  id: number;
  data: string;
  titulo: string;
  descricao: string;
  responsavel: string;
};

type NotaFiscalEmitida = {
  id: number;
  numeroNota: string;
  serie: string;
  tipo: TipoNotaFiscal;
  cliente: string;
  cnpjCpfCliente: string;
  municipioPrestacao: string;
  ufPrestacao: string;
  competencia: string;
  dataEmissao: string;
  valorBruto: number;
  valorLiquido: number;
  iss: number;
  retencoesFederais: number;
  situacaoFiscal: SituacaoFiscal;
  statusEnvioCliente: StatusEnvioCliente;
  statusVinculoRecebivel: StatusVinculoRecebivel;
  contaReceberCodigo: string | null;
  contaReceberVencimento: string | null;
  contaReceberValor: number | null;
  codigoServico: string;
  descricaoServico: string;
  centroCusto: string;
  projeto: string;
  responsavel: string;
  observacao: string | null;
  documentos: DocumentoFiscal[];
  procedimentoEnvio: ProcedimentoEnvioItem[];
  historico: HistoricoNota[];
};

type FilterState = {
  busca: string;
  situacaoFiscal: string;
  statusEnvioCliente: string;
  statusVinculoRecebivel: string;
  cliente: string;
  competencia: string;
};

type NotasFiscaisEmitidasLayoutProps = {
  section?: SectionKey;
};

const DEPARTMENT_COLOR = "#2f3f59";

const DOCUMENTOS_FISCAIS: Array<{
  tipo: TipoDocumentoFiscal;
  label: string;
}> = [
  { tipo: "pdf", label: "PDF da nota" },
  { tipo: "xml", label: "XML da nota" },
  { tipo: "cancelamento", label: "Cancelamento" },
  { tipo: "substituicao", label: "Substituição" },
];

const fixedNotas: NotaFiscalEmitida[] = [
  {
    id: 1,
    numeroNota: "000000124",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "Googlemarine Serviços Marítimos Ltda",
    cnpjCpfCliente: "12.345.678/0001-90",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-03",
    valorBruto: 12500,
    valorLiquido: 12187.5,
    iss: 312.5,
    retencoesFederais: 0,
    situacaoFiscal: "regular",
    statusEnvioCliente: "concluido",
    statusVinculoRecebivel: "recebivel_recebido",
    contaReceberCodigo: "CR-2026-0001",
    contaReceberVencimento: "2026-05-18",
    contaReceberValor: 12187.5,
    codigoServico: "14.01",
    descricaoServico:
      "Serviço de manutenção, reparo e testes em equipamento eletrônico marítimo.",
    centroCusto: "Laboratório / Reparos",
    projeto: "Case GM-2026-014",
    responsavel: "Financeiro",
    observacao:
      "Cliente exige envio do PDF ao solicitante e XML ao fiscal cadastrado.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0001.pdf",
        anexadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0001.xml",
        anexadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "solicitante",
        descricao: "Enviar PDF da nota ao solicitante do serviço.",
        documento: "pdf",
        contato: "solicitante@googlemarine.com",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        destino: "fiscal_cliente",
        descricao: "Enviar XML da nota para o departamento fiscal do cliente.",
        documento: "xml",
        contato: "fiscal@googlemarine.com",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-05-03",
        responsavel: "Financeiro",
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-03",
        titulo: "Nota emitida",
        descricao: "NFS-e emitida e documentos fiscais anexados.",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        data: "2026-05-03",
        titulo: "Envio concluído",
        descricao: "PDF enviado ao solicitante e XML enviado ao fiscal.",
        responsavel: "Financeiro",
      },
      {
        id: 3,
        data: "2026-05-08",
        titulo: "Recebível baixado",
        descricao: "Conta a receber vinculada foi marcada como recebida.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 2,
    numeroNota: "000000125",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "Bravante Navegação S.A.",
    cnpjCpfCliente: "33.333.333/0001-33",
    municipioPrestacao: "Rio de Janeiro",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-05",
    valorBruto: 18750,
    valorLiquido: 18281.25,
    iss: 468.75,
    retencoesFederais: 0,
    situacaoFiscal: "regular",
    statusEnvioCliente: "parcial",
    statusVinculoRecebivel: "recebivel_aberto",
    contaReceberCodigo: "CR-2026-0002",
    contaReceberVencimento: "2026-05-20",
    contaReceberValor: 18281.25,
    codigoServico: "14.01",
    descricaoServico:
      "Reparo técnico, testes funcionais e emissão de relatório para equipamento de comunicação.",
    centroCusto: "Operações / Marítimo",
    projeto: "Case BV-2026-021",
    responsavel: "Financeiro",
    observacao:
      "Procedimento do cliente exige envio pelo portal e cópia para o fiscal.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0002.pdf",
        anexadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0002.xml",
        anexadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "portal_cliente",
        descricao: "Lançar PDF e XML no portal de fornecedores do cliente.",
        documento: "pdf_xml",
        contato: "Portal Bravante",
        canal: "portal",
        status: "enviado",
        enviadoEm: "2026-05-05",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        destino: "fiscal_cliente",
        descricao: "Enviar cópia do XML para o e-mail fiscal.",
        documento: "xml",
        contato: "fiscal@bravante.com.br",
        canal: "email",
        status: "pendente",
        enviadoEm: null,
        responsavel: null,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-05",
        titulo: "Nota emitida",
        descricao: "NFS-e emitida com PDF e XML anexados.",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        data: "2026-05-05",
        titulo: "Envio parcial",
        descricao:
          "Documentos enviados no portal. Pendente envio de XML ao fiscal.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 3,
    numeroNota: "000000126",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "Marlin Navegação Ltda",
    cnpjCpfCliente: "44.444.444/0001-44",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-07",
    valorBruto: 9800,
    valorLiquido: 9555,
    iss: 245,
    retencoesFederais: 0,
    situacaoFiscal: "regular",
    statusEnvioCliente: "pendente_validacao",
    statusVinculoRecebivel: "recebivel_vencido",
    contaReceberCodigo: "CR-2026-0003",
    contaReceberVencimento: "2026-05-09",
    contaReceberValor: 9555,
    codigoServico: "14.01",
    descricaoServico:
      "Serviço de diagnóstico, inspeção e manutenção de controlador marítimo.",
    centroCusto: "Laboratório / Reparos",
    projeto: "Case ML-2026-008",
    responsavel: "Financeiro",
    observacao:
      "Cliente informa que o fiscal deve validar o recebimento da nota após envio.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0003.pdf",
        anexadoEm: "2026-05-07",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0003.xml",
        anexadoEm: "2026-05-07",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "solicitante",
        descricao: "Enviar PDF da nota ao solicitante.",
        documento: "pdf",
        contato: "operacoes@marlin.com.br",
        canal: "email",
        status: "enviado",
        enviadoEm: "2026-05-07",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        destino: "fiscal_cliente",
        descricao: "Enviar PDF e XML ao fiscal e aguardar confirmação.",
        documento: "pdf_xml",
        contato: "fiscal@marlin.com.br",
        canal: "email",
        status: "pendente",
        enviadoEm: null,
        responsavel: null,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-07",
        titulo: "Nota emitida",
        descricao: "Nota emitida e vinculada ao recebível.",
        responsavel: "Financeiro",
      },
      {
        id: 2,
        data: "2026-05-07",
        titulo: "Envio ao solicitante",
        descricao: "PDF enviado ao solicitante. Pendente validação fiscal.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 4,
    numeroNota: "000000127",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "OSM Thome Brasil",
    cnpjCpfCliente: "55.555.555/0001-55",
    municipioPrestacao: "Macaé",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-08",
    valorBruto: 6200,
    valorLiquido: 6045,
    iss: 155,
    retencoesFederais: 0,
    situacaoFiscal: "aguardando_xml",
    statusEnvioCliente: "nao_iniciado",
    statusVinculoRecebivel: "sem_recebivel",
    contaReceberCodigo: null,
    contaReceberVencimento: null,
    contaReceberValor: null,
    codigoServico: "1.05",
    descricaoServico:
      "Licenciamento e suporte operacional de módulo web para governança documental.",
    centroCusto: "Tecnologia / Logvi",
    projeto: "Logvi Governança",
    responsavel: "Guilherme Brito",
    observacao:
      "Antes do envio, anexar XML e conferir se o cliente exige lançamento em portal.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0004.pdf",
        anexadoEm: "2026-05-08",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [
      {
        id: 1,
        destino: "fiscal_cliente",
        descricao: "Enviar PDF e XML ao fiscal do cliente.",
        documento: "pdf_xml",
        contato: "fiscal@osmthome.com",
        canal: "email",
        status: "pendente",
        enviadoEm: null,
        responsavel: null,
      },
    ],
    historico: [
      {
        id: 1,
        data: "2026-05-08",
        titulo: "Nota emitida",
        descricao: "Nota emitida, mas XML ainda não foi anexado.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 5,
    numeroNota: "RASCUNHO",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "Vision Marine Electronics",
    cnpjCpfCliente: "Não informado",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-09",
    valorBruto: 0,
    valorLiquido: 0,
    iss: 0,
    retencoesFederais: 0,
    situacaoFiscal: "rascunho",
    statusEnvioCliente: "nao_aplicavel",
    statusVinculoRecebivel: "sem_recebivel",
    contaReceberCodigo: null,
    contaReceberVencimento: null,
    contaReceberValor: null,
    codigoServico: "14.01",
    descricaoServico:
      "Rascunho de nota para serviço técnico ainda em validação comercial.",
    centroCusto: "Comercial / Propostas",
    projeto: "Proposta VM-2026-003",
    responsavel: "Financeiro",
    observacao:
      "Aguardando dados cadastrais completos e fechamento do valor final.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [],
    historico: [
      {
        id: 1,
        data: "2026-05-09",
        titulo: "Rascunho criado",
        descricao: "Nota em preparação aguardando dados do cliente.",
        responsavel: "Financeiro",
      },
    ],
  },
  {
    id: 6,
    numeroNota: "000000128",
    serie: "NFS-e",
    tipo: "servico",
    cliente: "Cliente Teste Cancelamento",
    cnpjCpfCliente: "00.000.000/0001-00",
    municipioPrestacao: "Niterói",
    ufPrestacao: "RJ",
    competencia: "2026-05",
    dataEmissao: "2026-05-09",
    valorBruto: 4500,
    valorLiquido: 4387.5,
    iss: 112.5,
    retencoesFederais: 0,
    situacaoFiscal: "cancelada",
    statusEnvioCliente: "nao_aplicavel",
    statusVinculoRecebivel: "sem_recebivel",
    contaReceberCodigo: null,
    contaReceberVencimento: null,
    contaReceberValor: null,
    codigoServico: "14.01",
    descricaoServico:
      "Nota cancelada por erro nos dados cadastrais informados pelo tomador.",
    centroCusto: "Financeiro",
    projeto: "Cancelamento interno",
    responsavel: "Financeiro",
    observacao:
      "Emitir nova nota após correção dos dados cadastrais do tomador.",
    documentos: [
      {
        tipo: "pdf",
        label: "PDF da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelada.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "xml",
        label: "XML da nota",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelada.xml",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "cancelamento",
        label: "Cancelamento",
        status: "anexado",
        arquivoNome: "NF-2026-0006-cancelamento.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "substituicao",
        label: "Substituição",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
    procedimentoEnvio: [],
    historico: [
      {
        id: 1,
        data: "2026-05-09",
        titulo: "Nota cancelada",
        descricao: "Cancelamento realizado por erro cadastral do tomador.",
        responsavel: "Financeiro",
      },
    ],
  },
];

const initialFilters: FilterState = {
  busca: "",
  situacaoFiscal: "",
  statusEnvioCliente: "",
  statusVinculoRecebivel: "",
  cliente: "",
  competencia: "",
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatCurrency(value: number | null) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function formatCompetencia(value: string) {
  const [year, month] = value.split("-");

  if (!year || !month) return value;

  return `${month}/${year}`;
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getTipoNotaLabel(tipo: TipoNotaFiscal) {
  switch (tipo) {
    case "servico":
      return "Serviço";
    case "produto":
      return "Produto";
    case "mista":
      return "Mista";
    default:
      return "Indefinido";
  }
}

function getSituacaoFiscalLabel(status: SituacaoFiscal) {
  switch (status) {
    case "rascunho":
      return "Rascunho";
    case "regular":
      return "Regular";
    case "aguardando_pdf":
      return "Aguardando PDF";
    case "aguardando_xml":
      return "Aguardando XML";
    case "inconsistente":
      return "Inconsistente";
    case "cancelada":
      return "Cancelada";
    case "substituida":
      return "Substituída";
    default:
      return "Indefinido";
  }
}

function getStatusEnvioClienteLabel(status: StatusEnvioCliente) {
  switch (status) {
    case "nao_iniciado":
      return "Não enviado";
    case "parcial":
      return "Envio parcial";
    case "concluido":
      return "Envio concluído";
    case "pendente_validacao":
      return "Pendente validação";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getStatusRecebivelLabel(status: StatusVinculoRecebivel) {
  switch (status) {
    case "sem_recebivel":
      return "Sem recebível";
    case "recebivel_aberto":
      return "A receber";
    case "recebivel_vencido":
      return "Vencido";
    case "recebivel_recebido":
      return "Recebido";
    default:
      return "Indefinido";
  }
}

function getDestinoLabel(destino: DestinoEnvio) {
  switch (destino) {
    case "solicitante":
      return "Solicitante";
    case "fiscal_cliente":
      return "Fiscal do cliente";
    case "compras_cliente":
      return "Compras do cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "financeiro_cliente":
      return "Financeiro do cliente";
    default:
      return "Destino";
  }
}

function getDocumentoEnvioLabel(documento: ProcedimentoEnvioItem["documento"]) {
  switch (documento) {
    case "pdf":
      return "PDF";
    case "xml":
      return "XML";
    case "pdf_xml":
      return "PDF + XML";
    case "outro":
      return "Outro";
    default:
      return "Documento";
  }
}

function getCanalLabel(canal: ProcedimentoEnvioItem["canal"]) {
  switch (canal) {
    case "email":
      return "E-mail";
    case "portal":
      return "Portal";
    case "whatsapp":
      return "WhatsApp";
    case "outro":
      return "Outro";
    default:
      return "Canal";
  }
}

function getSituacaoFiscalVisual(status: SituacaoFiscal) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <ShieldCheck size={12} />,
      };

    case "rascunho":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    case "aguardando_pdf":
    case "aguardando_xml":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "inconsistente":
    case "cancelada":
    case "substituida":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <ShieldAlert size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getEnvioClienteVisual(status: StatusEnvioCliente) {
  switch (status) {
    case "concluido":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <MailCheck size={12} />,
      };

    case "parcial":
    case "pendente_validacao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <MailQuestion size={12} />,
      };

    case "nao_iniciado":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <Send size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getRecebivelVisual(status: StatusVinculoRecebivel) {
  switch (status) {
    case "recebivel_recebido":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "recebivel_aberto":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
        icon: <WalletCards size={12} />,
      };

    case "recebivel_vencido":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "sem_recebivel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Link2 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getDocumentoStatusLabel(status: StatusDocumentoFiscal) {
  switch (status) {
    case "anexado":
      return "Anexado";
    case "pendente":
      return "Pendente";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getDocumentoStatusVisual(status: StatusDocumentoFiscal) {
  switch (status) {
    case "anexado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getItemEnvioVisual(status: StatusItemEnvio) {
  switch (status) {
    case "enviado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getItemEnvioLabel(status: StatusItemEnvio) {
  switch (status) {
    case "enviado":
      return "Enviado";
    case "pendente":
      return "Pendente";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getDocumento(nota: NotaFiscalEmitida, tipo: TipoDocumentoFiscal) {
  return nota.documentos.find((documento) => documento.tipo === tipo) || null;
}

function getDocumentosObrigatorios(nota: NotaFiscalEmitida) {
  if (nota.situacaoFiscal === "rascunho") {
    return [];
  }

  return DOCUMENTOS_FISCAIS.filter((required) => {
    if (required.tipo === "cancelamento") {
      return nota.situacaoFiscal === "cancelada";
    }

    if (required.tipo === "substituicao") {
      return nota.situacaoFiscal === "substituida";
    }

    return required.tipo === "pdf" || required.tipo === "xml";
  });
}

function getPendenciasDocumentais(nota: NotaFiscalEmitida) {
  return getDocumentosObrigatorios(nota).filter((required) => {
    const documento = getDocumento(nota, required.tipo);

    if (!documento) return true;

    return documento.status === "pendente";
  });
}

function getPendenciasEnvio(nota: NotaFiscalEmitida) {
  if (nota.statusEnvioCliente === "nao_aplicavel") {
    return [];
  }

  return nota.procedimentoEnvio.filter((item) => item.status === "pendente");
}

function getResumoPdfXml(nota: NotaFiscalEmitida) {
  if (nota.situacaoFiscal === "rascunho") {
    return "Não emitida";
  }

  const pdf = getDocumento(nota, "pdf");
  const xml = getDocumento(nota, "xml");

  const pdfOk = pdf?.status === "anexado";
  const xmlOk = xml?.status === "anexado";

  if (pdfOk && xmlOk) return "PDF/XML OK";
  if (pdfOk && !xmlOk) return "Sem XML";
  if (!pdfOk && xmlOk) return "Sem PDF";

  return "PDF/XML pendente";
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "pendencias":
      return "Pendências";
    case "enviadas":
      return "Enviadas";
    case "aguardando-envio":
      return "Aguardando envio";
    case "canceladas":
      return "Canceladas";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper: string;
  tone: "neutral" | "success" | "warning" | "danger";
  icon: React.ReactNode;
}> = ({ label, value, helper, tone, icon }) => {
  const iconStyle =
    tone === "success"
      ? styles.summaryIconOk
      : tone === "warning"
      ? styles.summaryIconWarning
      : tone === "danger"
      ? styles.summaryIconDanger
      : styles.summaryIcon;

  return (
    <div style={styles.summaryCard}>
      <div style={iconStyle}>{icon}</div>

      <div style={styles.summaryInfo}>
        <span style={styles.summaryLabel}>{label}</span>
        <strong style={styles.summaryValue}>{value}</strong>
        <span style={styles.summaryHelper}>{helper}</span>
      </div>
    </div>
  );
};

const NotaFiscalRow: React.FC<{
  nota: NotaFiscalEmitida;
  departmentColor: string;
  onOpen: (nota: NotaFiscalEmitida) => void;
}> = ({ nota, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  const fiscalVisual = getSituacaoFiscalVisual(nota.situacaoFiscal);
  const envioVisual = getEnvioClienteVisual(nota.statusEnvioCliente);
  const recebivelVisual = getRecebivelVisual(nota.statusVinculoRecebivel);

  const pendenciasDocumentais = getPendenciasDocumentais(nota).length;

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? toRgba(departmentColor, 0.05) : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      title="Duplo clique para abrir o detalhamento"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(nota)}
    >
      <td style={styles.td}>
        <div style={styles.nfCell}>
          <div
            style={{
              ...styles.nfIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <ReceiptText size={15} />
          </div>

          <div style={styles.nfInfo}>
            <strong
              style={{
                ...styles.nfNumber,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {nota.numeroNota}
            </strong>
            <span style={styles.mutedText}>{nota.serie}</span>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{nota.cliente}</div>
        <div style={styles.mutedText}>{nota.cnpjCpfCliente}</div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{formatDate(nota.dataEmissao)}</div>
        <div style={styles.mutedText}>
          Competência {formatCompetencia(nota.competencia)}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.valueText}>{formatCurrency(nota.valorBruto)}</div>
        <div style={styles.mutedText}>
          A receber {formatCurrency(nota.valorLiquido)}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{nota.codigoServico}</div>
        <div style={styles.mutedText}>{nota.projeto}</div>
      </td>

      <td style={styles.td}>
        <Badge
          background={fiscalVisual.background}
          color={fiscalVisual.color}
          border={fiscalVisual.border}
          icon={fiscalVisual.icon}
        >
          {getSituacaoFiscalLabel(nota.situacaoFiscal)}
        </Badge>
      </td>

      <td style={styles.td}>
        <Badge
          background={envioVisual.background}
          color={envioVisual.color}
          border={envioVisual.border}
          icon={envioVisual.icon}
        >
          {getStatusEnvioClienteLabel(nota.statusEnvioCliente)}
        </Badge>
      </td>

      <td style={styles.td}>
        <div style={styles.documentMini}>
          <span
            style={{
              color:
                getResumoPdfXml(nota) === "PDF/XML OK" ? "#166534" : "#9a3412",
            }}
          >
            {getResumoPdfXml(nota)}
          </span>
          <span>
            {pendenciasDocumentais}{" "}
            {plural(pendenciasDocumentais, "pendência", "pendências")}
          </span>
        </div>
      </td>

      <td style={styles.tdNoRightBorder}>
        <Badge
          background={recebivelVisual.background}
          color={recebivelVisual.color}
          border={recebivelVisual.border}
          icon={recebivelVisual.icon}
        >
          {getStatusRecebivelLabel(nota.statusVinculoRecebivel)}
        </Badge>
      </td>
    </tr>
  );
};

const NotaFiscalDetailOverlay: React.FC<{
  nota: NotaFiscalEmitida | null;
  departmentColor: string;
  onClose: () => void;
}> = ({ nota, departmentColor, onClose }) => {
  if (!nota) return null;

  const fiscalVisual = getSituacaoFiscalVisual(nota.situacaoFiscal);
  const envioVisual = getEnvioClienteVisual(nota.statusEnvioCliente);
  const recebivelVisual = getRecebivelVisual(nota.statusVinculoRecebivel);
  const totalTributos = nota.iss + nota.retencoesFederais;

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>
              Financeiro / Notas fiscais emitidas
            </div>

            <h2 style={styles.overlayTitle}>
              NF {nota.numeroNota} · {nota.cliente}
            </h2>

            <p style={styles.overlaySubtitle}>
              Dados fiscais, documentos, envio ao cliente, vínculo financeiro e
              histórico da nota.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailTopGrid}>
            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Valor da NF</span>
              <strong style={styles.detailValue}>
                {formatCurrency(nota.valorBruto)}
              </strong>
              <span style={styles.detailMeta}>
                A receber {formatCurrency(nota.valorLiquido)}
              </span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Situação fiscal</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={fiscalVisual.background}
                  color={fiscalVisual.color}
                  border={fiscalVisual.border}
                  icon={fiscalVisual.icon}
                >
                  {getSituacaoFiscalLabel(nota.situacaoFiscal)}
                </Badge>
              </div>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Envio ao cliente</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={envioVisual.background}
                  color={envioVisual.color}
                  border={envioVisual.border}
                  icon={envioVisual.icon}
                >
                  {getStatusEnvioClienteLabel(nota.statusEnvioCliente)}
                </Badge>
              </div>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Conta a receber</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={recebivelVisual.background}
                  color={recebivelVisual.color}
                  border={recebivelVisual.border}
                  icon={recebivelVisual.icon}
                >
                  {getStatusRecebivelLabel(nota.statusVinculoRecebivel)}
                </Badge>
              </div>
            </div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <ReceiptText size={15} />
                <strong>Dados da nota</strong>
              </div>

              <div style={styles.infoGridTwo}>
                <div style={styles.infoItem}>
                  <span>Número / Série</span>
                  <strong>
                    {nota.numeroNota} · {nota.serie}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Tipo</span>
                  <strong>{getTipoNotaLabel(nota.tipo)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Emissão</span>
                  <strong>{formatDate(nota.dataEmissao)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Competência</span>
                  <strong>{formatCompetencia(nota.competencia)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Município</span>
                  <strong>
                    {nota.municipioPrestacao}/{nota.ufPrestacao}
                  </strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Responsável</span>
                  <strong>{nota.responsavel}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <FileText size={15} />
                <strong>Tomador</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Cliente</span>
                  <strong>{nota.cliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>CNPJ / CPF</span>
                  <strong>{nota.cnpjCpfCliente}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Projeto / Case</span>
                  <strong>{nota.projeto}</strong>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <FileCheck2 size={15} />
              <strong>Serviço e valores</strong>
            </div>

            <div style={styles.serviceBox}>
              <div style={styles.serviceDescription}>
                <span>Serviço</span>
                <strong>{nota.codigoServico}</strong>
                <p>{nota.descricaoServico}</p>
              </div>

              <div style={styles.taxGrid}>
                <div style={styles.taxItem}>
                  <span>Valor da NF</span>
                  <strong>{formatCurrency(nota.valorBruto)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>ISS</span>
                  <strong>{formatCurrency(nota.iss)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>Retenções</span>
                  <strong>{formatCurrency(nota.retencoesFederais)}</strong>
                </div>

                <div style={styles.taxItem}>
                  <span>Total deduções</span>
                  <strong>{formatCurrency(totalTributos)}</strong>
                </div>

                <div style={styles.taxItemWide}>
                  <span>Valor líquido previsto</span>
                  <strong>{formatCurrency(nota.valorLiquido)}</strong>
                </div>
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <Paperclip size={15} />
              <strong>Documentos fiscais</strong>
            </div>

            <div style={styles.documentsGrid}>
              {DOCUMENTOS_FISCAIS.map((required) => {
                const documento = getDocumento(nota, required.tipo);
                const status = documento?.status || "pendente";
                const visual = getDocumentoStatusVisual(status);

                return (
                  <div key={required.tipo} style={styles.documentCard}>
                    <div style={styles.documentHeader}>
                      <div style={styles.documentTitleArea}>
                        <strong>{required.label}</strong>
                        <span>
                          {documento?.arquivoNome || "Nenhum arquivo anexado"}
                        </span>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getDocumentoStatusLabel(status)}
                      </Badge>
                    </div>

                    <div style={styles.documentFooter}>
                      <span>
                        Anexado em {formatDate(documento?.anexadoEm || null)}
                      </span>

                      <div style={styles.documentActions}>
                        <button
                          type="button"
                          style={{
                            ...styles.iconButton,
                            color:
                              status === "anexado"
                                ? departmentColor
                                : "#94a3b8",
                            borderColor:
                              status === "anexado"
                                ? toRgba(departmentColor, 0.35)
                                : "#e2e8f0",
                            cursor:
                              status === "anexado" ? "pointer" : "not-allowed",
                          }}
                          disabled={status !== "anexado"}
                        >
                          <Eye size={13} />
                        </button>

                        <button
                          type="button"
                          style={{
                            ...styles.iconButton,
                            color:
                              status === "anexado"
                                ? departmentColor
                                : "#94a3b8",
                            borderColor:
                              status === "anexado"
                                ? toRgba(departmentColor, 0.35)
                                : "#e2e8f0",
                            cursor:
                              status === "anexado" ? "pointer" : "not-allowed",
                          }}
                          disabled={status !== "anexado"}
                        >
                          <Download size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <MailCheck size={15} />
              <strong>Envio ao cliente</strong>
            </div>

            <div style={styles.envioIntro}>
              <strong>Procedimento aplicado pelo cadastro do cliente.</strong>
            </div>

            <div style={styles.envioGrid}>
              {nota.procedimentoEnvio.length === 0 && (
                <div style={styles.emptyEnvio}>
                  Nenhum procedimento de envio aplicável para esta nota.
                </div>
              )}

              {nota.procedimentoEnvio.map((item) => {
                const visual = getItemEnvioVisual(item.status);

                return (
                  <div key={item.id} style={styles.envioCard}>
                    <div style={styles.envioCardHeader}>
                      <div style={styles.envioTitleArea}>
                        <strong>{getDestinoLabel(item.destino)}</strong>
                        <span>{item.descricao}</span>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getItemEnvioLabel(item.status)}
                      </Badge>
                    </div>

                    <div style={styles.envioMetaGrid}>
                      <div>
                        <span>Documento</span>
                        <strong>
                          {getDocumentoEnvioLabel(item.documento)}
                        </strong>
                      </div>

                      <div>
                        <span>Canal</span>
                        <strong>{getCanalLabel(item.canal)}</strong>
                      </div>

                      <div>
                        <span>Contato / Portal</span>
                        <strong>{item.contato}</strong>
                      </div>

                      <div>
                        <span>Enviado em</span>
                        <strong>{formatDate(item.enviadoEm)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailGridTwo}>
            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <WalletCards size={15} />
                <strong>Vínculo financeiro</strong>
              </div>

              <div style={styles.infoGridOne}>
                <div style={styles.infoItem}>
                  <span>Conta a receber</span>
                  <strong>{nota.contaReceberCodigo || "Não vinculada"}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Vencimento</span>
                  <strong>{formatDate(nota.contaReceberVencimento)}</strong>
                </div>

                <div style={styles.infoItem}>
                  <span>Valor vinculado</span>
                  <strong>{formatCurrency(nota.contaReceberValor)}</strong>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <div style={styles.detailSectionHeader}>
                <Clock3 size={15} />
                <strong>Histórico</strong>
              </div>

              <div style={styles.historyList}>
                {nota.historico.map((item) => (
                  <div key={item.id} style={styles.historyItem}>
                    <div style={styles.historyDot} />
                    <div>
                      <strong>{item.titulo}</strong>
                      <span>
                        {formatDate(item.data)} · {item.responsavel}
                      </span>
                      <p>{item.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {nota.observacao && (
            <section style={styles.observationBox}>
              <strong>Observação interna</strong>
              <span>{nota.observacao}</span>
            </section>
          )}
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Fechar
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Send size={14} />
            Registrar envio
          </button>

          <button type="button" style={styles.secondaryButton}>
            <Link2 size={14} />
            Vincular recebível
          </button>

          <button type="button" style={styles.secondaryDangerButton}>
            <RefreshCcw size={14} />
            Cancelar/Substituir
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              background: departmentColor,
              borderColor: departmentColor,
            }}
          >
            <Upload size={14} />
            Anexar PDF/XML
          </button>
        </div>
      </div>
    </div>
  );
};

const NotasFiscaisEmitidasLayout: React.FC<NotasFiscaisEmitidasLayoutProps> = ({
  section,
}) => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedNota, setSelectedNota] = useState<NotaFiscalEmitida | null>(
    null
  );

  const clientes = useMemo(() => {
    return Array.from(new Set(fixedNotas.map((nota) => nota.cliente))).sort(
      (a, b) => a.localeCompare(b, "pt-BR")
    );
  }, []);

  const competencias = useMemo(() => {
    return Array.from(new Set(fixedNotas.map((nota) => nota.competencia))).sort(
      (a, b) => b.localeCompare(a)
    );
  }, []);

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "pendencias":
        return fixedNotas.filter(
          (nota) =>
            nota.situacaoFiscal !== "regular" ||
            getPendenciasDocumentais(nota).length > 0 ||
            getPendenciasEnvio(nota).length > 0
        );

      case "enviadas":
        return fixedNotas.filter(
          (nota) => nota.statusEnvioCliente === "concluido"
        );

      case "aguardando-envio":
        return fixedNotas.filter(
          (nota) =>
            nota.statusEnvioCliente === "nao_iniciado" ||
            nota.statusEnvioCliente === "parcial" ||
            nota.statusEnvioCliente === "pendente_validacao"
        );

      case "canceladas":
        return fixedNotas.filter(
          (nota) =>
            nota.situacaoFiscal === "cancelada" ||
            nota.situacaoFiscal === "substituida"
        );

      case "visao-geral":
      default:
        return fixedNotas;
    }
  }, [activeSection]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);

    return filteredBySection.filter((nota) => {
      const buscaMatch =
        !busca ||
        [
          nota.numeroNota,
          nota.serie,
          nota.cliente,
          nota.cnpjCpfCliente,
          nota.municipioPrestacao,
          nota.ufPrestacao,
          nota.codigoServico,
          nota.descricaoServico,
          nota.centroCusto,
          nota.projeto,
          nota.responsavel,
          nota.contaReceberCodigo,
        ].some((field) => normalizeTextSearch(field).includes(busca));

      const fiscalMatch =
        !filters.situacaoFiscal ||
        nota.situacaoFiscal === filters.situacaoFiscal;

      const envioMatch =
        !filters.statusEnvioCliente ||
        nota.statusEnvioCliente === filters.statusEnvioCliente;

      const recebivelMatch =
        !filters.statusVinculoRecebivel ||
        nota.statusVinculoRecebivel === filters.statusVinculoRecebivel;

      const clienteMatch = !filters.cliente || nota.cliente === filters.cliente;

      const competenciaMatch =
        !filters.competencia || nota.competencia === filters.competencia;

      return (
        buscaMatch &&
        fiscalMatch &&
        envioMatch &&
        recebivelMatch &&
        clienteMatch &&
        competenciaMatch
      );
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    const notasEmitidasMes = fixedNotas.filter(
      (nota) =>
        nota.competencia === "2026-05" &&
        nota.situacaoFiscal !== "rascunho" &&
        nota.situacaoFiscal !== "cancelada" &&
        nota.situacaoFiscal !== "substituida"
    );

    const valorFaturado = notasEmitidasMes.reduce(
      (sum, nota) => sum + nota.valorBruto,
      0
    );

    const pendenciasDocumentais = fixedNotas.filter(
      (nota) =>
        nota.situacaoFiscal !== "rascunho" &&
        getPendenciasDocumentais(nota).length > 0
    ).length;

    const pendenciasEnvio = fixedNotas.filter(
      (nota) => getPendenciasEnvio(nota).length > 0
    ).length;

    const canceladasSubstituidas = fixedNotas.filter(
      (nota) =>
        nota.situacaoFiscal === "cancelada" ||
        nota.situacaoFiscal === "substituida"
    ).length;

    return {
      emitidasMes: notasEmitidasMes.length,
      valorFaturado,
      pendenciasDocumentais,
      pendenciasEnvio,
      canceladasSubstituidas,
    };
  }, []);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Notas fiscais emitidas - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.summaryGrid}>
          <SummaryCard
            label="Emitidas no mês"
            value={resumo.emitidasMes}
            helper={`${resumo.canceladasSubstituidas} cancelada(s) ou substituída(s)`}
            tone="neutral"
            icon={<ReceiptText size={16} />}
          />

          <SummaryCard
            label="Valor faturado"
            value={formatCurrency(resumo.valorFaturado)}
            helper="Total bruto válido na competência atual"
            tone="success"
            icon={<WalletCards size={16} />}
          />

          <SummaryCard
            label="Pendências documentais"
            value={resumo.pendenciasDocumentais}
            helper="PDF, XML, cancelamento ou substituição"
            tone={resumo.pendenciasDocumentais > 0 ? "warning" : "neutral"}
            icon={<Paperclip size={16} />}
          />

          <SummaryCard
            label="Pendências de envio"
            value={resumo.pendenciasEnvio}
            helper="Procedimentos ainda não concluídos"
            tone={resumo.pendenciasEnvio > 0 ? "warning" : "neutral"}
            icon={<MailQuestion size={16} />}
          />
        </section>

        <section style={styles.actionBar}>
          <div style={styles.actionBarInfo}>
            <strong>Controle fiscal e operacional de notas emitidas</strong>
            <span>
              Acompanhe emissão, documentos fiscais, envio ao cliente e vínculo
              com contas a receber.
            </span>
          </div>

          <div style={styles.actionButtons}>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
            >
              <Plus size={14} />
              Nova nota
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Upload size={14} />
              Importar XML
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Paperclip size={14} />
              Anexar PDF/XML
            </button>

            <button type="button" style={styles.secondaryButton}>
              <Link2 size={14} />
              Vincular recebível
            </button>
          </div>
        </section>

        <section style={styles.filterPanel}>
          <div style={styles.filterHeader}>
            <div style={styles.filterTitleArea}>
              <Filter size={15} />
              <strong>Filtros</strong>
            </div>

            <button
              type="button"
              style={styles.clearButton}
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>

          <div style={styles.filterGrid}>
            <div style={styles.searchField}>
              <Search size={14} />
              <input
                style={styles.searchInput}
                value={filters.busca}
                onChange={(event) => updateFilter("busca", event.target.value)}
                placeholder="Buscar por NF, cliente, CNPJ, serviço, projeto ou recebível..."
              />
            </div>

            <select
              style={styles.filterInput}
              value={filters.situacaoFiscal}
              onChange={(event) =>
                updateFilter("situacaoFiscal", event.target.value)
              }
            >
              <option value="">Todas as situações fiscais</option>
              <option value="rascunho">Rascunho</option>
              <option value="regular">Regular</option>
              <option value="aguardando_pdf">Aguardando PDF</option>
              <option value="aguardando_xml">Aguardando XML</option>
              <option value="inconsistente">Inconsistente</option>
              <option value="cancelada">Cancelada</option>
              <option value="substituida">Substituída</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.statusEnvioCliente}
              onChange={(event) =>
                updateFilter("statusEnvioCliente", event.target.value)
              }
            >
              <option value="">Todos os envios</option>
              <option value="nao_iniciado">Não enviado</option>
              <option value="parcial">Envio parcial</option>
              <option value="concluido">Envio concluído</option>
              <option value="pendente_validacao">Pendente validação</option>
              <option value="nao_aplicavel">Não aplicável</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.statusVinculoRecebivel}
              onChange={(event) =>
                updateFilter("statusVinculoRecebivel", event.target.value)
              }
            >
              <option value="">Todos os vínculos financeiros</option>
              <option value="sem_recebivel">Sem recebível</option>
              <option value="recebivel_aberto">A receber</option>
              <option value="recebivel_vencido">Vencido</option>
              <option value="recebivel_recebido">Recebido</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.cliente}
              onChange={(event) => updateFilter("cliente", event.target.value)}
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente} value={cliente}>
                  {cliente}
                </option>
              ))}
            </select>

            <select
              style={styles.filterInput}
              value={filters.competencia}
              onChange={(event) =>
                updateFilter("competencia", event.target.value)
              }
            >
              <option value="">Todas as competências</option>
              {competencias.map((competencia) => (
                <option key={competencia} value={competencia}>
                  {formatCompetencia(competencia)}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Notas fiscais emitidas</h2>
              <span style={styles.tableHint}>
                {filteredItems.length}{" "}
                {plural(filteredItems.length, "registro", "registros")}{" "}
                encontrado(s). Duplo clique na linha abre o detalhamento.
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>NF</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Emissão / Competência</th>
                  <th style={styles.th}>Valor</th>
                  <th style={styles.th}>Serviço / Projeto</th>
                  <th style={styles.th}>Situação fiscal</th>
                  <th style={styles.th}>Envio</th>
                  <th style={styles.th}>PDF/XML</th>
                  <th style={styles.thNoRightBorder}>Conta a receber</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((nota) => (
                  <NotaFiscalRow
                    key={nota.id}
                    nota={nota}
                    departmentColor={activeDepartmentColor}
                    onOpen={setSelectedNota}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>
                      Nenhuma nota fiscal encontrada com os filtros atuais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <NotaFiscalDetailOverlay
        nota={selectedNota}
        departmentColor={activeDepartmentColor}
        onClose={() => setSelectedNota(null)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  summaryCard: {
    minHeight: 78,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "11px 12px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },

  summaryInfo: {
    minWidth: 0,
  },

  summaryIcon: {
    width: 34,
    height: 34,
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconOk: {
    width: 34,
    height: 34,
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconWarning: {
    width: 34,
    height: 34,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconDanger: {
    width: 34,
    height: 34,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryLabel: {
    display: "block",
    fontSize: 11.2,
    fontWeight: 900,
    color: "#64748b",
    lineHeight: 1.15,
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  summaryValue: {
    display: "block",
    marginTop: 3,
    fontSize: 16,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.15,
  },

  summaryHelper: {
    display: "block",
    marginTop: 4,
    fontSize: 11.1,
    fontWeight: 700,
    color: "#64748b",
    lineHeight: 1.2,
  },

  actionBar: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  actionBarInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 240,
    color: "#334155",
    fontSize: 12.4,
    fontWeight: 700,
  },

  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
    flexWrap: "wrap",
  },

  filterPanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
  },

  filterHeader: {
    minHeight: 38,
    padding: "7px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  filterTitleArea: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  clearButton: {
    height: 28,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    padding: "0 9px",
    fontSize: 11.5,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  filterGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1.45fr repeat(5, minmax(132px, 1fr))",
    gap: 8,
  },

  searchField: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 9px",
    minWidth: 0,
  },

  searchInput: {
    border: 0,
    outline: "none",
    height: "100%",
    flex: 1,
    minWidth: 0,
    fontFamily: "inherit",
    fontSize: 12.3,
    fontWeight: 750,
    color: "#111827",
  },

  filterInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 8px",
    fontFamily: "inherit",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    minWidth: 0,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 44,
    padding: "8px 10px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  tableTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 950,
    color: "#111827",
  },

  tableHint: {
    display: "block",
    marginTop: 1,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1370,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  thNoRightBorder: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: 0,
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
    transition: "background 140ms ease, box-shadow 140ms ease",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  tdNoRightBorder: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: 0,
    overflow: "hidden",
  },

  nfCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  nfIcon: {
    width: 29,
    height: 29,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  nfInfo: {
    minWidth: 0,
  },

  nfNumber: {
    fontSize: 12.8,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mainText: {
    fontSize: 12.4,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mutedText: {
    marginTop: 1,
    display: "block",
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  valueText: {
    fontSize: 12.8,
    fontWeight: 950,
    color: "#111827",
    whiteSpace: "nowrap",
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  documentMini: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 11.5,
    fontWeight: 850,
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(1080px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    fontSize: 12.4,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dde5",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  detailContent: {
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  detailTopGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  detailCard: {
    minHeight: 74,
    border: "1px solid #dfe3e8",
    background: "#f8fafc",
    padding: "10px 11px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
  },

  detailLabel: {
    fontSize: 10.8,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  detailValue: {
    marginTop: 3,
    fontSize: 13.2,
    fontWeight: 950,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailMeta: {
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailBadgeArea: {
    marginTop: 7,
  },

  detailGridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  detailSection: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    minWidth: 0,
  },

  detailSectionHeader: {
    minHeight: 38,
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  infoGridTwo: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  infoGridOne: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  infoItem: {
    minHeight: 52,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
  },

  serviceBox: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr",
    gap: 10,
  },

  serviceDescription: {
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  taxGrid: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },

  taxItem: {
    minHeight: 54,
    borderRight: "1px solid #edf0f2",
    borderBottom: "1px solid #edf0f2",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
  },

  taxItemWide: {
    minHeight: 54,
    gridColumn: "1 / -1",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
  },

  documentsGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  documentCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  documentHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },

  documentTitleArea: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
  },

  documentFooter: {
    borderTop: "1px solid #edf0f2",
    paddingTop: 7,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    color: "#64748b",
    fontSize: 11.2,
    fontWeight: 750,
  },

  documentActions: {
    display: "flex",
    gap: 5,
  },

  iconButton: {
    width: 26,
    height: 24,
    border: "1px solid",
    background: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
  },

  envioIntro: {
    margin: 10,
    marginBottom: 0,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  envioGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  envioCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 9,
  },

  envioCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },

  envioTitleArea: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  envioMetaGrid: {
    borderTop: "1px solid #edf0f2",
    paddingTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
  },

  emptyEnvio: {
    gridColumn: "1 / -1",
    padding: 16,
    textAlign: "center",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 12.5,
    fontWeight: 750,
  },

  historyList: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  historyItem: {
    display: "grid",
    gridTemplateColumns: "12px 1fr",
    gap: 8,
    color: "#334155",
    fontSize: 12.1,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  historyDot: {
    width: 8,
    height: 8,
    marginTop: 5,
    borderRadius: 999,
    background: "#94a3b8",
  },

  observationBox: {
    border: "1px solid #dfe3e8",
    background: "#f8fafc",
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.3,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  overlayFooter: {
    minHeight: 48,
    padding: "10px 18px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  primaryButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #d7dde5",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  secondaryDangerButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #fecaca",
    background: "#ffffff",
    color: "#991b1b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  cancelButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
};

export default NotasFiscaisEmitidasLayout;
